import { useEffect, useState } from "react";

import { Trans } from "@lingui/react/macro";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe as StripeJs } from "@stripe/stripe-js";
import { Loader2 } from "lucide-react";

let stripePromise: Promise<StripeJs | null> | null = null;

async function getStripe(): Promise<StripeJs | null> {
  if (stripePromise) return stripePromise;

  const envKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  if (envKey) {
    stripePromise = loadStripe(envKey);
    return stripePromise;
  }

  return null;
}

interface StripeElementsProviderProps {
  children: React.ReactNode;
  clientSecret?: string;
}

export function StripeElementsProvider({ children, clientSecret }: StripeElementsProviderProps) {
  const [stripe, setStripe] = useState<StripeJs | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    getStripe().then((s) => {
      if (s) setStripe(s);
      else setUnavailable(true);
    });
  }, []);

  // Missing VITE_STRIPE_PUBLISHABLE_KEY or Stripe.js failed to load —
  // surface it instead of rendering nothing (an invisible add-card form
  // reads as a dead button).
  if (unavailable) {
    return (
      <div className="border-warning/40 bg-warning-soft text-warning-strong rounded-lg border px-4 py-3 text-sm">
        <Trans>Payment system is being set up. Please try again in a few minutes.</Trans>
      </div>
    );
  }

  if (!stripe) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
      </div>
    );
  }

  const options = clientSecret ? { clientSecret } : undefined;

  return (
    <Elements stripe={stripe} options={options}>
      {children}
    </Elements>
  );
}
